'use client';
import React, { useState, useEffect, useCallback } from 'react';
import {
  DocumentTextIcon,
  LinkIcon,
  ArrowDownTrayIcon,
  FolderOpenIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { createClient } from '@/lib/supabase/client';

interface CourseResource {
  id: string;
  course_id: string;
  title: string;
  description: string | null;
  resource_type: string;
  file_url: string | null;
  file_name: string | null;
  file_size_bytes: number | null;
  external_url: string | null;
  topic: string | null;
  week_number: number | null;
  created_at: string;
}

interface EnrolledCourse {
  id: string;
  code: string;
  name: string;
}

interface Props {
  studentId: string;
}

const RESOURCE_TYPE_LABELS: Record<string, string> = {
  file: 'File',
  link: 'Link',
  video: 'Video',
  document: 'Document',
  assessment_brief: 'Assessment Brief',
  lesson_plan: 'Lesson Plan',
  handout: 'Handout',
  reading_list: 'Reading List',
  other: 'Other',
};

const RESOURCE_TYPE_COLORS: Record<string, string> = {
  file: 'bg-blue-100 text-blue-700',
  link: 'bg-purple-100 text-purple-700',
  video: 'bg-red-100 text-red-700',
  document: 'bg-indigo-100 text-indigo-700',
  assessment_brief: 'bg-orange-100 text-orange-700',
  lesson_plan: 'bg-green-100 text-green-700',
  handout: 'bg-teal-100 text-teal-700',
  reading_list: 'bg-yellow-100 text-yellow-700',
  other: 'bg-gray-100 text-gray-700',
};

function formatBytes(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function ResourceIcon({ type }: { type: string }) {
  if (type === 'link' || type === 'video') return <LinkIcon className="w-4 h-4" />;
  return <DocumentTextIcon className="w-4 h-4" />;
}

export default function StudentCourseResourcesTab({ studentId }: Props) {
  const [courses, setCourses] = useState<EnrolledCourse[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState<string>('');
  const [resources, setResources] = useState<CourseResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [collapsedTopics, setCollapsedTopics] = useState<Set<string>>(new Set());

  // Fetch enrolled courses
  const fetchCourses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const supabase = createClient();
      const { data, error: err } = await supabase
        .from('enrollments')
        .select('courses(id, code, name)')
        .eq('student_id', studentId)
        .neq('enrollment_status', 'withdrawn');
      if (err) throw new Error(err.message);
      const courseList: EnrolledCourse[] = (data || [])
        .map((e: any) => e.courses)
        .filter(Boolean);
      setCourses(courseList);
      if (courseList.length > 0) setSelectedCourseId(courseList[0].id);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => { fetchCourses(); }, [fetchCourses]);

  // Fetch resources for selected course
  const fetchResources = useCallback(async () => {
    if (!selectedCourseId) return;
    setLoading(true);
    setError(null);
    try {
      const supabase = createClient();
      const { data, error: err } = await supabase
        .from('course_resources')
        .select('id, course_id, title, description, resource_type, file_url, file_name, file_size_bytes, external_url, topic, week_number, created_at')
        .eq('course_id', selectedCourseId)
        .eq('is_published', true)
        .order('week_number', { ascending: true, nullsFirst: false })
        .order('sort_order')
        .order('created_at');
      if (err) throw new Error(err.message);
      setResources(data || []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [selectedCourseId]);

  useEffect(() => { fetchResources(); }, [fetchResources]);

  function toggleTopic(topic: string) {
    setCollapsedTopics((prev) => {
      const next = new Set(prev);
      if (next.has(topic)) next.delete(topic);
      else next.add(topic);
      return next;
    });
  }

  const filtered = resources.filter((r) =>
    !search || r.title.toLowerCase().includes(search.toLowerCase()) ||
    r.description?.toLowerCase().includes(search.toLowerCase()) ||
    r.topic?.toLowerCase().includes(search.toLowerCase())
  );

  const grouped = filtered.reduce<Record<string, CourseResource[]>>((acc, r) => {
    const key = r.topic ? r.topic : r.week_number ? `Week ${r.week_number}` : 'General';
    if (!acc[key]) acc[key] = [];
    acc[key].push(r);
    return acc;
  }, {});

  const selectedCourse = courses.find((c) => c.id === selectedCourseId);

  if (loading && courses.length === 0) {
    return (
      <div className="p-5 space-y-3">
        {[0, 1, 2].map((i) => <div key={i} className="h-12 bg-muted/40 rounded-xl animate-pulse" />)}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5">
        <div className="bg-[var(--negative-bg)] border border-red-200 rounded-xl p-3 text-sm text-negative">{error}</div>
      </div>
    );
  }

  if (courses.length === 0) {
    return (
      <div className="p-5 text-center py-12">
        <FolderOpenIcon className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
        <p className="text-sm font-600 text-foreground">No enrolled courses</p>
        <p className="text-xs text-muted-foreground mt-1">Course resources will appear here once you are enrolled</p>
      </div>
    );
  }

  return (
    <div className="p-5 space-y-4">
      {/* Controls */}
      <div className="flex items-center gap-3 flex-wrap">
        <select
          className="text-sm border border-border rounded-lg px-3 py-2 bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          value={selectedCourseId}
          onChange={(e) => { setSelectedCourseId(e.target.value); setSearch(''); }}
        >
          {courses.map((c) => (
            <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
          ))}
        </select>
        <div className="relative flex-1 min-w-[180px]">
          <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search resources…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
      </div>

      {/* Resource count */}
      {!loading && (
        <p className="text-xs text-muted-foreground">
          {filtered.length} resource{filtered.length !== 1 ? 's' : ''} available for <span className="font-600">{selectedCourse?.code}</span>
        </p>
      )}

      {/* Loading */}
      {loading && (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => <div key={i} className="h-12 bg-muted/40 rounded-xl animate-pulse" />)}
        </div>
      )}

      {/* Empty state */}
      {!loading && filtered.length === 0 && (
        <div className="text-center py-10">
          <FolderOpenIcon className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">
            {search ? 'No resources match your search' : 'No resources published yet for this course'}
          </p>
        </div>
      )}

      {/* Grouped Resources */}
      {!loading && Object.entries(grouped).map(([topic, items]) => (
        <div key={topic} className="border border-border rounded-xl overflow-hidden">
          <button
            onClick={() => toggleTopic(topic)}
            className="w-full flex items-center justify-between px-4 py-3 bg-muted/30 hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              {collapsedTopics.has(topic) ? (
                <ChevronRightIcon className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronDownIcon className="w-4 h-4 text-muted-foreground" />
              )}
              <span className="text-sm font-700 text-foreground">{topic}</span>
              <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{items.length}</span>
            </div>
          </button>

          {!collapsedTopics.has(topic) && (
            <div className="divide-y divide-border">
              {items.map((r) => (
                <div key={r.id} className="flex items-center gap-3 px-4 py-3 hover:bg-muted/20 transition-colors">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${RESOURCE_TYPE_COLORS[r.resource_type] || 'bg-gray-100 text-gray-700'}`}>
                    <ResourceIcon type={r.resource_type} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-600 text-foreground">{r.title}</p>
                      <span className={`text-xs px-1.5 py-0.5 rounded font-500 ${RESOURCE_TYPE_COLORS[r.resource_type] || 'bg-gray-100 text-gray-700'}`}>
                        {RESOURCE_TYPE_LABELS[r.resource_type] || r.resource_type}
                      </span>
                    </div>
                    {r.description && (
                      <p className="text-xs text-muted-foreground mt-0.5">{r.description}</p>
                    )}
                    {r.file_name && (
                      <p className="text-xs text-muted-foreground mt-0.5">{r.file_name}{r.file_size_bytes ? ` · ${formatBytes(r.file_size_bytes)}` : ''}</p>
                    )}
                  </div>
                  {(r.file_url || r.external_url) && (
                    <a
                      href={r.file_url || r.external_url || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-xs font-600 text-primary bg-secondary hover:bg-primary hover:text-white px-3 py-1.5 rounded-lg transition-colors flex-shrink-0"
                    >
                      <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                      {r.resource_type === 'link' ? 'Open' : 'Download'}
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
